import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import Pagination from '@/components/base/Pagination';
import DramaCard from '@/components/feature/DramaCard';
import EpisodeCard from '@/components/feature/EpisodeCard';
import { useFavorites } from '@/hooks/useFavorites';
import { useSeo } from '@/hooks/useSeo';
import { dramas, type Drama } from '@/mocks/dramas';
import { getDramaById, searchDramas, searchEpisodes } from '@/lib/content';
import FilterBar, { type FilterState } from './components/FilterBar';

const PAGE_SIZE = 6;

export default function Category() {
  useSeo({
    title: '迷人的保姆剧集列表 - 剧集分类与更新状态筛选',
    description:
      '迷人的保姆剧集列表面向观众提供剧集筛选与排序功能，支持按更新时间、集数顺序浏览，并可查看连载中与已完结的剧集信息。',
    keywords: '迷人的保姆剧集列表,迷人的保姆分类,电视剧在线观看,剧集更新状态,家庭情感剧集',
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const { favorites, isFavorite, toggleFavorite } = useFavorites();

  const queryKeyword = searchParams.get('q') ?? '';
  const favoritesParam = searchParams.get('filter') === 'favorites';

  const [keyword, setKeyword] = useState(queryKeyword);
  const [filters, setFilters] = useState<FilterState>({
    status: 'all',
    sort: 'newest',
    favoritesOnly: favoritesParam,
  });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setKeyword(queryKeyword);
    if (favoritesParam) {
      setFilters((prev) => ({ ...prev, favoritesOnly: true }));
    }
  }, [queryKeyword, favoritesParam]);

  useEffect(() => {
    setLoading(true);
    setError('');
    const timer = window.setTimeout(() => {
      try {
        // 数据为本地内容，此处模拟一次加载过程以展示加载状态
        setLoading(false);
      } catch {
        setError('剧集数据加载失败，请稍后重试。');
        setLoading(false);
      }
    }, 320);
    return () => window.clearTimeout(timer);
  }, [queryKeyword, filters.status, filters.sort, filters.favoritesOnly]);

  const handleSearchSubmit = (event: FormEvent) => {
    event.preventDefault();
    const q = keyword.trim();
    const next = new URLSearchParams(searchParams);
    if (q) next.set('q', q);
    else next.delete('q');
    next.delete('filter');
    setSearchParams(next);
    setPage(1);
  };

  const filteredDramas = useMemo(() => {
    let list: Drama[] = [...dramas];
    if (filters.favoritesOnly) {
      list = list.filter((d) => favorites.includes(d.id));
    }
    if (filters.status !== 'all') {
      list = list.filter((d) => d.status === filters.status);
    }
    list.sort((a, b) => {
      if (filters.sort === 'episodes') return b.totalEpisodes - a.totalEpisodes;
      return a.updatedAt < b.updatedAt ? 1 : -1;
    });
    return list;
  }, [filters, favorites]);

  const totalPages = Math.max(1, Math.ceil(filteredDramas.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pagedDramas = filteredDramas.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const matchedDramas = queryKeyword ? searchDramas(queryKeyword) : [];
  const matchedEpisodes = queryKeyword ? searchEpisodes(queryKeyword) : [];

  const resetFilters = () => {
    setFilters({ status: 'all', sort: 'newest', favoritesOnly: false });
    setKeyword('');
    setSearchParams({});
    setPage(1);
  };

  const handleFavoritesToggle = () => {
    setFilters((prev) => ({ ...prev, favoritesOnly: !prev.favoritesOnly }));
    setPage(1);
  };

  return (
    <div className="w-full">
      <section className="w-full bg-primary-950 py-10 md:py-14">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="[&_a]:text-background-300 [&_span]:text-background-200 [&_.text-foreground-900]:!text-background-50 [&_.text-foreground-400]:!text-primary-500">
            <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '剧集分类' }]} />
          </div>
          <h1 className="mt-5 text-2xl md:text-4xl font-bold text-background-50">剧集分类</h1>
          <p className="mt-3 text-sm md:text-base text-background-300 max-w-3xl leading-relaxed">
            这里汇总了《迷人的保姆》及同类型家庭情感剧集。你可以按更新状态筛选、按更新时间或集数顺序排序，
            也可以直接搜索剧集或章节关键词，快速找到想看的内容。
          </p>

          <form onSubmit={handleSearchSubmit} className="mt-6 max-w-xl relative">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              type="text"
              placeholder="输入剧集名称、章节标题或演员关键词"
              className="w-full h-12 pl-11 pr-28 text-sm rounded-full bg-background-50 border border-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-accent-300"
            />
            <button
              type="submit"
              className="absolute right-1.5 top-1/2 -translate-y-1/2 h-9 px-5 rounded-full bg-accent-500 text-background-50 dark:text-foreground-950 text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap"
            >
              搜索
            </button>
          </form>
        </div>
      </section>

      <section className="w-full bg-background-50 py-8 md:py-12">
        <div className="w-full px-4 md:px-8 lg:px-12">
          {queryKeyword && (
            <div className="mb-6 flex flex-wrap items-center gap-3">
              <span className="text-sm text-foreground-600">
                关键词「<strong className="text-primary-700">{queryKeyword}</strong>」共找到{' '}
                <strong className="text-primary-700 font-label">{matchedDramas.length}</strong> 部剧集、
                <strong className="text-primary-700 font-label">{matchedEpisodes.length}</strong> 个章节
              </span>
              <button
                type="button"
                onClick={resetFilters}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border border-background-300 text-foreground-600 hover:border-accent-300 hover:text-accent-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-close-line"></i>
                清除搜索
              </button>
            </div>
          )}

          {queryKeyword ? (
            <div className="flex flex-col gap-10">
              <div>
                <h2 className="text-lg font-bold text-foreground-950 mb-4">相关剧集</h2>
                {matchedDramas.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                    {matchedDramas.map((drama) => (
                      <DramaCard key={drama.id} drama={drama} />
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-foreground-500">没有找到匹配的剧集。</p>
                )}
              </div>
              <div>
                <h2 className="text-lg font-bold text-foreground-950 mb-4">相关章节</h2>
                {matchedEpisodes.length > 0 ? (
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                    {matchedEpisodes.map((episode) => (
                      <EpisodeCard
                        key={episode.id}
                        episode={episode}
                        dramaTitle={getDramaById(episode.dramaId)?.title}
                      />
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-foreground-500">没有找到匹配的章节。</p>
                )}
              </div>
              {matchedDramas.length === 0 && matchedEpisodes.length === 0 && (
                <EmptyState onReset={resetFilters} />
              )}
            </div>
          ) : (
            <>
              <FilterBar
                filters={filters}
                onStatusChange={(v) => {
                  setFilters((prev) => ({ ...prev, status: v }));
                  setPage(1);
                }}
                onSortChange={(v) => {
                  setFilters((prev) => ({ ...prev, sort: v }));
                  setPage(1);
                }}
                onFavoritesToggle={handleFavoritesToggle}
                onReset={resetFilters}
                resultCount={filteredDramas.length}
              />

              <div className="mt-8">
                {loading ? (
                  <LoadingGrid />
                ) : error ? (
                  <ErrorState message={error} onRetry={() => setPage(1)} />
                ) : pagedDramas.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-3 gap-4 md:gap-6">
                    {pagedDramas.map((drama) => (
                      <div key={drama.id} className="relative">
                        <DramaCard drama={drama} />
                        <button
                          type="button"
                          onClick={() => toggleFavorite(drama.id)}
                          aria-label={isFavorite(drama.id) ? '取消收藏' : '收藏'}
                          className={`absolute bottom-[68px] right-3 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                            isFavorite(drama.id)
                              ? 'bg-accent-500 text-background-50 dark:text-foreground-950'
                              : 'bg-background-50/90 text-foreground-600 hover:text-accent-600'
                          }`}
                        >
                          <i className={isFavorite(drama.id) ? 'ri-heart-3-fill' : 'ri-heart-3-line'}></i>
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <EmptyState onReset={resetFilters} />
                )}
              </div>

              {!loading && !error && (
                <Pagination page={safePage} totalPages={totalPages} onChange={setPage} />
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}

function LoadingGrid() {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="rounded-lg overflow-hidden border border-background-200 animate-soft-pulse">
          <div className="w-full aspect-[3/4] bg-background-200"></div>
          <div className="p-3.5 space-y-2">
            <div className="h-4 w-3/4 rounded bg-background-200"></div>
            <div className="h-3 w-1/2 rounded bg-background-200"></div>
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ onReset }: { onReset: () => void }) {
  return (
    <div className="w-full rounded-lg border border-dashed border-background-300 bg-background-50 py-16 px-6 text-center">
      <span className="mx-auto w-16 h-16 rounded-full bg-background-100 flex items-center justify-center text-foreground-400 mb-4">
        <i className="ri-inbox-archive-line text-3xl"></i>
      </span>
      <h3 className="text-lg font-semibold text-foreground-900">没有找到匹配的内容</h3>
      <p className="mt-2 text-sm text-foreground-500 max-w-md mx-auto">
        试试更换关键词，或重置当前的筛选条件后再看看。
      </p>
      <button
        type="button"
        onClick={onReset}
        className="mt-6 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
      >
        <i className="ri-refresh-line"></i>
        重置筛选条件
      </button>
    </div>
  );
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="w-full rounded-lg border border-accent-300 bg-accent-50 py-16 px-6 text-center">
      <span className="mx-auto w-16 h-16 rounded-full bg-accent-100 flex items-center justify-center text-accent-700 mb-4">
        <i className="ri-error-warning-line text-3xl"></i>
      </span>
      <h3 className="text-lg font-semibold text-foreground-900">加载出错</h3>
      <p className="mt-2 text-sm text-foreground-600 max-w-md mx-auto">{message}</p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-6 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-accent-500 text-background-50 dark:text-foreground-950 text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap"
      >
        <i className="ri-refresh-line"></i>
        重新加载
      </button>
    </div>
  );
}